import React from 'react';
import Link from 'next/link';
import { TrendingUp, Users, Bell, BarChart2, ArrowRight, Tag, Eye, MousePointerClick } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const perks = [
  { id: 'p1', icon: TrendingUp, title: 'Reach nearby customers', desc: 'Your offers are shown to customers actively looking for deals in your area.' },
  { id: 'p2', icon: Users, title: 'Build loyal regulars', desc: 'Repeat redemptions and customer insights help you build a loyal local base.' },
  { id: 'p3', icon: Bell, title: 'Real-time notifications', desc: 'Get notified when customers claim your offers so you can prepare.' },
  { id: 'p4', icon: BarChart2, title: 'Track performance', desc: 'See views, clicks, and redemptions for every offer you post — all in one dashboard.' },
];

const featureHighlights = [
  { id: 'fh-1', icon: Eye, label: 'Track offer views', color: 'text-blue-500', bg: 'bg-blue-50' },
  { id: 'fh-2', icon: MousePointerClick, label: 'Monitor redemptions', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { id: 'fh-3', icon: Tag, label: 'Manage all offers', color: 'text-primary', bg: 'bg-primary/10' },
  { id: 'fh-4', icon: BarChart2, label: 'Performance insights', color: 'text-violet-500', bg: 'bg-violet-50' },
];

export default function LandingForBusiness() {
  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <p className="section-label mb-3">For business owners</p>
            <h2 className="text-hero-md text-foreground mb-5">
              Grow your local business with{' '}
              <span className="gradient-brand-text">OFY for Business</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8">
              Post offers, attract nearby customers, and track redemptions — all from one simple dashboard. No marketing degree required.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {perks?.map((perk) => {
                const Icon = perk?.icon;
                return (
                  <div key={perk?.id} className="flex gap-3">
                    <div className="w-9 h-9 rounded-lg gradient-brand-soft flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={17} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-0.5">{perk?.title}</p>
                      <p className="text-sm text-muted-foreground">{perk?.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Link href="/business-sign-up-login" className="btn-primary text-base px-6 py-3.5 inline-flex">
              Get Started Free
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="relative">
            <div className="card-elevated rounded-3xl p-5 sm:p-8 bg-white overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Business Dashboard</p>
                  <p className="text-base sm:text-lg font-bold text-foreground mt-0.5">Everything in one place</p>
                </div>
                <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center shrink-0">
                  <BarChart2 size={18} className="text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {featureHighlights?.map((f) => {
                  const FIcon = f?.icon;
                  return (
                    <div key={f?.id} className={`rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 ${f?.bg}`}>
                      <FIcon size={18} className={`${f?.color} shrink-0`} />
                      <p className="text-xs sm:text-sm font-semibold text-foreground leading-snug">{f?.label}</p>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-xl border border-border p-3 sm:p-4 flex flex-col gap-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How it works for businesses</p>
                {[
                  { step: '1', text: 'Sign up and create your business profile' },
                  { step: '2', text: 'Post offers with discounts, deals, or promotions' },
                  { step: '3', text: 'Customers near your pin code discover your offers' },
                  { step: '4', text: 'Track views and redemptions in real time' },
                ]?.map((item) => (
                  <div key={`step-${item?.step}`} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full gradient-brand flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-white">{item?.step}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug">{item?.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}